<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  customAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  sourceChannel: { type: String, default: '' },
  lastConversationAt: { type: Number, default: null },
  createdAt: { type: Number, default: null },
  captainSummary: { type: String, default: '' },
  priority: { type: String, default: '' },
  assignee: { type: Object, default: null },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const channelIcon = computed(() =>
  props.sourceChannel ? getInboxIconByType(props.sourceChannel) : null
);

const CHANNEL_LABELS = {
  'Channel::WebWidget': 'Website',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::FacebookPage': 'Facebook',
  'Channel::Instagram': 'Instagram',
  'Channel::Email': 'Email',
  'Channel::Api': 'API',
  'Channel::TwilioSms': 'SMS',
  'Channel::Telegram': 'Telegram',
  'Channel::Line': 'Line',
  'Channel::Sms': 'SMS',
  'Channel::Tiktok': 'TikTok',
  'Channel::Voip': 'Voice',
};
const channelLabel = computed(
  () =>
    CHANNEL_LABELS[props.sourceChannel] ||
    props.sourceChannel?.replace('Channel::', '') ||
    ''
);

const stage = computed(() => props.customAttributes?.stage || '');
const verificationStatus = computed(
  () => props.customAttributes?.verificationStatus || ''
);
const nextFollowUpAt = computed(
  () => props.customAttributes?.nextFollowUpAt || ''
);

const VERIFICATION_COLORS = {
  'Pending Review': 'amber',
  Verified: 'teal',
  Junk: 'ruby',
};
const verificationColor = computed(
  () => VERIFICATION_COLORS[verificationStatus.value] || 'slate'
);

const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};
const PRIORITY_COLORS = {
  urgent: 'ruby',
  high: 'amber',
  medium: 'blue',
  low: 'slate',
};
const priorityLabel = computed(() =>
  props.priority ? t(PRIORITY_LABEL_KEYS[props.priority]) : ''
);
const priorityColor = computed(
  () => PRIORITY_COLORS[props.priority] || 'slate'
);

const formattedLastConversationAt = computed(() =>
  props.lastConversationAt ? dynamicTime(props.lastConversationAt) : ''
);
const formattedCreatedAt = computed(() =>
  props.createdAt ? dynamicTime(props.createdAt) : ''
);
const formattedNextFollowUpAt = computed(() =>
  nextFollowUpAt.value
    ? new Date(nextFollowUpAt.value).toLocaleDateString()
    : ''
);

const captainSummarySnippet = computed(() => {
  if (!props.captainSummary) return '';
  const lines = props.captainSummary.split('\n').filter(Boolean).slice(0, 2);
  return lines.join(' ');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <!-- Header: avatar, identity, badges -->
      <div class="flex items-start justify-start w-full gap-4">
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="42"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col flex-1 gap-1.5">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ name }}
            </span>
            <span
              v-if="additionalAttributes?.companyName"
              class="inline-flex items-center gap-1"
            >
              <span class="i-ph-building-light size-4 text-n-slate-10 mb-0.5" />
              <span class="text-sm truncate text-n-slate-11">
                {{ additionalAttributes.companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-n-slate-10"
          >
            <span v-if="email" class="truncate">{{ email }}</span>
            <span v-if="email && phoneNumber" class="w-px h-3 bg-n-slate-6" />
            <span v-if="phoneNumber" class="truncate">{{ phoneNumber }}</span>
          </div>
          <div class="flex flex-wrap items-center gap-1.5">
            <span v-if="sourceChannel" class="inline-flex items-center gap-1">
              <Label :label="channelLabel" color="slate" compact>
                <template #icon>
                  <Icon :icon="channelIcon" class="size-3.5" />
                </template>
              </Label>
            </span>
            <span v-if="priority" class="inline-flex items-center gap-1">
              <Label :label="priorityLabel" :color="priorityColor" compact>
                <template #icon>
                  <CardPriorityIcon :priority="priority" class="!size-3.5" />
                </template>
              </Label>
            </span>
            <span
              v-if="verificationStatus"
              class="inline-flex items-center gap-1"
            >
              <Label
                :label="verificationStatus"
                :color="verificationColor"
                compact
              />
            </span>
            <span v-if="stage" class="inline-flex items-center gap-1">
              <Label :label="stage" color="iris" compact />
            </span>
          </div>
        </div>
      </div>

      <!-- Captain Summary -->
      <div
        class="w-full pl-[3.5rem] text-sm italic text-n-slate-10 line-clamp-2"
      >
        {{
          captainSummarySnippet ||
          t('CONTACTS_LAYOUT.CARD.CAPTAIN_SUMMARY_EMPTY')
        }}
      </div>

      <!-- Assigned Agent / Last Conversation / Next Follow-up / Created -->
      <div
        class="flex flex-wrap items-center gap-x-4 gap-y-1 pl-[3.5rem] text-sm text-n-slate-11"
      >
        <span v-if="assignee" class="inline-flex items-center gap-1.5">
          <Avatar :name="assignee.name" :src="assignee.thumbnail" :size="16" />
          {{ assignee.name }}
        </span>
        <span v-if="formattedLastConversationAt">
          {{
            t('CONTACTS_LAYOUT.CARD.LEAD_INFO.LAST_CONVERSATION', {
              time: formattedLastConversationAt,
            })
          }}
        </span>
        <span v-if="formattedNextFollowUpAt">
          {{
            t('CONTACTS_LAYOUT.CARD.LEAD_INFO.NEXT_FOLLOW_UP', {
              date: formattedNextFollowUpAt,
            })
          }}
        </span>
        <span v-if="formattedCreatedAt">
          {{
            t('CONTACTS_LAYOUT.CARD.LEAD_INFO.CREATED', {
              time: formattedCreatedAt,
            })
          }}
        </span>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end w-full gap-2 pt-1">
        <Button
          :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
          variant="faded"
          color="slate"
          size="sm"
          @click="onClickViewDetails"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.CARD.EXPAND')"
          icon="i-lucide-chevron-down"
          variant="faded"
          color="slate"
          size="sm"
          :class="{ 'rotate-180': isExpanded }"
          @click="onClickExpand"
        />
        <ComposeConversation :contact-id="String(id)">
          <template #trigger>
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
              size="sm"
            />
          </template>
        </ComposeConversation>
      </div>

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
              <ContactsForm
                ref="contactsFormRef"
                :contact-data="contactData"
                @update="handleFormUpdate"
              />
              <div>
                <Button
                  :label="
                    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                  "
                  size="sm"
                  :is-loading="isUpdating"
                  :disabled="isUpdating || isFormInvalid"
                  @click="handleUpdateContact"
                />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
